import { SHOPIFY_STOREFRONT_GRAPHQL_API, SHOPIFY_STOREFRONT_TOKEN } from "@/config";
import { GET_PRODUCT_BY_HANDLE, GET_PRODUCTS_BY_MONTH } from "@/lib/shopify/queries";
import { IGProduct, IGCollectionsProduct, TTrackTypes } from "@/types";
import { getAdminIdFromStoreFrontId } from "./shopify";
import { DateTime } from "luxon";
import { getOrdinalSuffix } from "./utils";

/**
 * Fetches a product by handle from Shopify's GraphQL API
 * @param productHandle - The product handle to fetch
 * @returns Promise<IGProduct | null> - The product data or null if not found
 */
export const fetchProductByHandle = async (productHandle: string): Promise<IGProduct | null> => {
  try {
    const response = await fetch(SHOPIFY_STOREFRONT_GRAPHQL_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: GET_PRODUCT_BY_HANDLE,
        variables: {
          handle: productHandle,
        },
      }),
    });

    const data = await response.json();
    let product: any = data?.data?.product || {};

    if (!product.id) {
      return null;
    }

    product.variants = product?.variants?.edges?.map((e: any) => e.node) || [];
    product.collections =
      product?.collections && product?.collections?.nodes?.map((collection: any) => (collection.id ? getAdminIdFromStoreFrontId(collection.id as string) : collection.id));

    return product as IGProduct;
  } catch (error) {
    console.error("Error fetching product:", error);
    return null;
  }
};

export const fetchProductsByMonth = async (track: TTrackTypes, date: DateTime): Promise<IGCollectionsProduct[] | null> => {
  try {
    const trackMapping: Record<TTrackTypes, string> = {
      MAIN_TRACK: "Main Track",
      INTERMEDIATE_TRACK: "Intermediate Track",
      MINI_TRACK: "Mini Track",
      JUNIOR_TRACK: "Junior Track",
      BATTLE_KART_TRACK: "BattleKart",
      RACING_GAMES_TRACK: "Racing Games Track",
      ARCADE: "Arcade",
    };

    const currentMonthDate = date;
    const nextMonthDate = date.plus({ month: 1 });
    const lastMonthDate = date.minus({ month: 1 }).endOf("month");
    const twoMonthsAheadDate = date.plus({ month: 2 }).startOf("month");

    const getDateStr = (date: DateTime) => {
      const day = date.toFormat("d");
      const suffix = getOrdinalSuffix(parseInt(day));
      return `${day}${suffix} ` + date.toFormat(`MMMM yyyy`);
    };

    const last7DaysOfLastMonth = new Array(7).fill(0).map((_, i) => getDateStr(lastMonthDate.minus({ day: i })));
    const next7DaysOfTwoMonthsAhead = new Array(7).fill(0).map((_, i) => getDateStr(twoMonthsAheadDate.plus({ day: i })));

    const orQuery = last7DaysOfLastMonth.map((day) => `title:'${day}'`).join(" OR ") + " OR " + next7DaysOfTwoMonthsAhead.map((day) => `title:'${day}'`).join(" OR ");

    // Properly quote product_type values that contain spaces to ensure correct Shopify search behavior
    const productType = trackMapping[track];
    const quotedProductType = productType.includes(" ") ? `"${productType}"` : productType;

    // Query for both current month and next month
    const query = `product_type:${quotedProductType} AND (title:'${currentMonthDate.toFormat("MMMM yyyy")}' OR title:'${nextMonthDate.toFormat("MMMM yyyy")}' OR ${orQuery})`;

    console.log(`🔍 Fetching products for ${productType}:`);
    console.log(`   Query: ${query}`);

    const response = await fetch(SHOPIFY_STOREFRONT_GRAPHQL_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: GET_PRODUCTS_BY_MONTH,
        variables: {
          query,
        },
      }),
    });

    const data = await response.json();

    if (data.errors) {
      console.error("GraphQL Error:", data.errors);
      return null;
    }

    const products: any = data?.data?.products?.nodes || [];
    console.log(`📦 Fetched ${products.length} products for ${trackMapping[track]} in ${date.toFormat("MMMM yyyy")}`);

    // Log first and last product dates for verification
    if (products.length > 0) {
      const firstProduct = products[0];
      const lastProduct = products[products.length - 1];
      console.log(`   First: "${firstProduct.title}"`);
      console.log(`   Last: "${lastProduct.title}"`);
    }

    return products;
  } catch (error) {
    console.error("Error fetching products by month:", error);
    return null;
  }
};

/**
 * Enhanced function to fetch collections using the working ID format
 * @param handleOrId - Collection handle (e.g., "mainTrack") or ID (e.g., "277341896864")
 * @returns Promise<IGCollectionsProduct[] | null> - The collection products or null if not found
 */
/**
 * Parse date from product title using Luxon with multiple format attempts
 * @param title - Product title like "Friday, 7th November 2025 | 10 Minute Race"
 * @returns Luxon DateTime object or null if parsing fails
 */
export const parseDateFromTitle = (title: string): DateTime | null => {
  try {
    // Extract date from title (before the "|" character)
    const datePart = title.split("|")[0].trim();

    // Remove ordinal suffixes (st, nd, rd, th)
    const cleanDate = datePart.replace(/(\d+)(st|nd|rd|th)/, "$1");

    // Try different formats
    const formats = [
      "cccc, d MMMM yyyy", // "Friday, 7 November 2025"
      "cccc, dd MMMM yyyy", // "Friday, 07 November 2025"
      "cccc, d MMM yyyy", // "Friday, 7 Nov 2025"
      "d MMMM yyyy", // "7 November 2025"
      "dd MMMM yyyy", // "07 November 2025"
      "d MMM yyyy", // "7 Nov 2025"
      "yyyy-MM-dd", // "2025-11-07"
      "dd/MM/yyyy", // "07/11/2025"
      "MM/dd/yyyy", // "11/07/2025"
    ];

    for (const format of formats) {
      const parsed = DateTime.fromFormat(cleanDate, format);
      if (parsed.isValid) {
        return parsed;
      }
    }

    // Try parsing just the date part after comma
    const dateOnly = cleanDate.split(", ")[1];
    if (dateOnly) {
      for (const format of ["d MMMM yyyy", "dd MMMM yyyy", "d MMM yyyy"]) {
        const parsed = DateTime.fromFormat(dateOnly, format);
        if (parsed.isValid) {
          return parsed;
        }
      }
    }

    return null;
  } catch (error) {
    console.warn("Date parsing error for title:", title, error);
    return null;
  }
};

/**
 * Price thresholds for different attractions
 */
const PRICE_THRESHOLDS = {
  mainTrack: { cheap: 29, medium: 39 },
  miniTrack: { cheap: 17, medium: 22 },
  juniorTrack: { cheap: 19, medium: 29 },
  battlekart: { cheap: 19, medium: 29 },
  intermediateTrack: { cheap: 19, medium: 24 },
} as const;

/**
 * Color codes for price categories
 */
const PRICE_COLORS = {
  cheapest: "#01a698",
  medium: "#ff9400",
  expensive: "#d1435b",
  unavailable: "transparent", // No background color for unavailable dates
} as const;

/**
 * Calculate weighted average price for variants on a specific date
 * Formula: (sum of (seats_remaining * price)) / total_seats_available
 * This calculates the weighted average based on inventory levels
 */
const calculateWeightedAveragePriceForDate = (variants: any[]): number | null => {
  if (!variants || variants.length === 0) return null;

  let totalWeightedPrice = 0;
  let totalSeatsAvailable = 0;
  let hasAvailableSeats = false;

  variants.forEach((variant: any, index: number) => {
    // Only count available variants with quantity > 0
    if (variant.availableForSale && variant.quantityAvailable > 0) {
      const price = parseFloat(variant.priceV2?.amount || "0");
      const quantity = variant.quantityAvailable;

      if (price > 0) {
        totalWeightedPrice += price * quantity;
        totalSeatsAvailable += quantity;
        hasAvailableSeats = true;
      }
    }
  });

  // Return null if no seats are available (will result in no background color)
  if (!hasAvailableSeats || totalSeatsAvailable === 0) {
    return null;
  }

  const averagePrice = totalWeightedPrice / totalSeatsAvailable;
  return averagePrice;
};

/**
 * Get price category based on weighted average price and attraction type
 */
const getPriceCategory = (averagePrice: number | null, attractionType: keyof typeof PRICE_THRESHOLDS): keyof typeof PRICE_COLORS => {
  if (averagePrice === null) {
    return "unavailable";
  }

  const thresholds = PRICE_THRESHOLDS[attractionType];

  if (averagePrice <= thresholds.cheap) {
    return "cheapest";
  } else if (averagePrice <= thresholds.medium) {
    return "medium";
  } else {
    return "expensive";
  }
};

/**
 * Get background color for a calendar date based on price category
 */
const getCalendarDateColor = (averagePrice: number | null, attractionType: keyof typeof PRICE_THRESHOLDS): string => {
  const category = getPriceCategory(averagePrice, attractionType);
  return PRICE_COLORS[category];
};

/**
 * Calculate price data for all dates in a collection
 * Returns a map of date -> { averagePrice, color, category }
 */
export const calculatePricingDataForCollection = (
  products: IGCollectionsProduct[],
  attractionType: keyof typeof PRICE_THRESHOLDS
): Record<string, { averagePrice: number | null; color: string; category: string }> => {
  const startTime = performance.now();
  console.log(`🚀 Starting pricing calculation for ${products.length} products (${attractionType})`);

  const dateMap: Record<string, any[]> = {};

  // Group all variants by date (using product date, not variant date)
  products.forEach((product: any) => {
    const productDate = parseDateFromTitle(product.title || "");
    if (!productDate) {
      console.log(`❌ Failed to parse date from product: "${product.title}"`);
      return;
    }

    const dateString = productDate.toFormat("yyyy-MM-dd");
    if (!dateMap[dateString]) {
      dateMap[dateString] = [];
    }

    // Add all variants for this product to the date
    // The variants are the different race types/times for this date
    if (product.variants?.nodes) {
      dateMap[dateString].push(...product.variants.nodes);
    }
  });

  // Calculate pricing data for each date
  const pricingData: Record<string, { averagePrice: number | null; color: string; category: string }> = {};

  Object.keys(dateMap).forEach((dateString) => {
    const variants = dateMap[dateString];

    const averagePrice = calculateWeightedAveragePriceForDate(variants);
    const category = getPriceCategory(averagePrice, attractionType);
    const color = getCalendarDateColor(averagePrice, attractionType);

    pricingData[dateString] = {
      averagePrice,
      color,
      category,
    };
  });

  const endTime = performance.now();
  const duration = endTime - startTime;

  // Log detailed breakdown of dates and their pricing
  const sortedDates = Object.keys(pricingData).sort();
  console.log(`✅ Pricing calculation completed in ${duration.toFixed(2)}ms`);
  console.log(`📊 ${Object.keys(pricingData).length} dates with pricing data:`);
  console.log(`   Date range: ${sortedDates[0] || "none"} to ${sortedDates[sortedDates.length - 1] || "none"}`);

  // Group by category for summary
  const categoryCount = { cheapest: 0, medium: 0, expensive: 0, unavailable: 0 };
  Object.values(pricingData).forEach((data) => {
    categoryCount[data.category as keyof typeof categoryCount]++;
  });
  console.log(`   Categories: $=${categoryCount.cheapest}, $$=${categoryCount.medium}, $$$=${categoryCount.expensive}, unavailable=${categoryCount.unavailable}`);

  return pricingData;
};

